/**
 * Turn raw `lastError` strings from chat status into user-facing copy.
 * Prefer nested `.message` (e.g. API `error.message`) over dumping full JSON.
 */
export function formatStackFrameLastErrorForDisplay(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return raw;
  }

  const withoutNonErrorPrefix = trimmed
    .replace(/^Non-Error value:\s*/i, '')
    .trim();

  const tryParse = (s: string): unknown => {
    try {
      return JSON.parse(s) as unknown;
    } catch {
      return undefined;
    }
  };

  let parsed: unknown = tryParse(withoutNonErrorPrefix);
  if (parsed === undefined) {
    parsed = tryParse(trimmed);
  }
  if (parsed === undefined) {
    return raw;
  }

  if (typeof parsed === 'string') {
    return parsed;
  }

  const messageFromObject = (obj: unknown): string | undefined => {
    if (obj === null || typeof obj !== 'object') {
      return undefined;
    }
    const o = obj as Record<string, unknown>;
    if (typeof o['message'] === 'string' && o['message'].length > 0) {
      return o['message'];
    }
    const err = o['error'];
    if (err !== null && typeof err === 'object') {
      const nested = (err as Record<string, unknown>)['message'];
      if (typeof nested === 'string' && nested.length > 0) {
        return nested;
      }
    }
    return undefined;
  };

  const msg = messageFromObject(parsed);
  if (msg !== undefined) {
    return msg;
  }

  try {
    return JSON.stringify(parsed, null, 2);
  } catch {
    return raw;
  }
}
